import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Row,
  Col,
  Button,
  Spin,
  Input,
  Grid,
  message,
  Table,
  Tag,
  Tooltip,
  Space,
} from "antd";
import {
  ReloadOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// Token + axios instance (same pattern as other pages)
const getStoredToken = () => {
  const keys = ["token", "accessToken", "AuthToken", "jwtToken"];
  for (const k of keys) {
    const v = localStorage.getItem(k);
    if (v) return v.replace(/^"|"$/g, "");
  }
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.token || user?.accessToken || null;
  } catch {
    return null;
  }
};

const api = axios.create({
  baseURL: global.config?.APIURL?.BaseURL || "/",
  timeout: 15000,
});
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`,
    };
  }
  if (config.withCredentials === undefined) config.withCredentials = false;
  return config;
});

// Helpers
const formatDateTime = (dt) => {
  if (!dt) return "-";
  try {
    const d = new Date(dt);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(dt);
  }
};

const timeAgo = (dt) => {
  if (!dt) return "-";
  const now = Date.now();
  const t = new Date(dt).getTime();
  const diff = Math.max(0, Math.floor((now - t) / 1000));
  if (diff < 60) return `${diff}s ago`;
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const UserActivityOverView = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Data
  const [userCounts, setUserCounts] = useState([]); // GetRecentActivityCountByUser
  const [recentActivities, setRecentActivities] = useState([]); // GetRecentActivities

  // UI state
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [searchUsers, setSearchUsers] = useState("");
  const [searchActivities, setSearchActivities] = useState("");

  useEffect(() => {
    loadAll(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async (initial = false) => {
    try {
      if (initial) setLoading(true);
      else setFetching(true);

      const [countsRes, activitiesRes] = await Promise.all([
        api.get("api/UserActivity/GetRecentActivityCountByUser"),
        api.get("api/UserActivity/GetRecentActivities"),
      ]);

      setUserCounts(Array.isArray(countsRes.data) ? countsRes.data : []);
      setRecentActivities(
        Array.isArray(activitiesRes.data) ? activitiesRes.data : []
      );
    } catch (err) {
      if (err?.response?.status === 401) {
        messageApi.error("Unauthorized. Please log in again.");
      } else if (err?.request && !err?.response) {
        messageApi.error("Network/CORS error while loading activity.");
      } else {
        messageApi.error("Failed to load activity data.");
      }
    } finally {
      setLoading(false);
      setFetching(false);
    }
  };

  // Derived data
  const filteredUserCounts = useMemo(() => {
    const q = searchUsers.trim().toLowerCase();
    const list = Array.isArray(userCounts) ? userCounts : [];
    const result = q
      ? list.filter((u) => (u?.Name || "").toLowerCase().includes(q))
      : list;
    // Sort by RecentActivityCount desc, then TotalActivityCount, then LastActiveAt desc
    return [...result].sort((a, b) => {
      const r = (b.RecentActivityCount || 0) - (a.RecentActivityCount || 0);
      if (r !== 0) return r;
      const t = (b.TotalActivityCount || 0) - (a.TotalActivityCount || 0);
      if (t !== 0) return t;
      return new Date(b.LastActiveAt || 0) - new Date(a.LastActiveAt || 0);
    });
  }, [userCounts, searchUsers]);

  const filteredActivities = useMemo(() => {
    const q = searchActivities.trim().toLowerCase();
    const list = Array.isArray(recentActivities) ? recentActivities : [];
    if (!q) return list;
    return list.filter((a) =>
      [a?.Name, a?.ScreenName, a?.DetailName, a?.Detail]
        .filter(Boolean)
        .some((x) => String(x).toLowerCase().includes(q))
    );
  }, [recentActivities, searchActivities]);

  // Columns
  const userColumns = [
    {
      title: "User",
      dataIndex: "Name",
      key: "Name",
      sorter: (a, b) => (a?.Name || "").localeCompare(b?.Name || ""),
      width: 220,
      render: (name) => <Text strong>{name || "Unknown"}</Text>,
      ellipsis: true,
    },
    {
      title: "Last Active",
      dataIndex: "LastActiveAt",
      key: "LastActiveAt",
      width: 220,
      sorter: (a, b) =>
        new Date(a?.LastActiveAt || 0).getTime() -
        new Date(b?.LastActiveAt || 0).getTime(),
      render: (dt) => {
        const full = formatDateTime(dt);
        const rel = timeAgo(dt);
        return (
          <Space size={4} direction="vertical">
            <Tooltip title={full}>
              <Text>
                <ClockCircleOutlined /> {rel}
              </Text>
            </Tooltip>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {full}
            </Text>
          </Space>
        );
      },
    },
    {
      title: "Recent",
      dataIndex: "RecentActivityCount",
      key: "RecentActivityCount",
      width: 110,
      align: "right",
      sorter: (a, b) =>
        (a?.RecentActivityCount || 0) - (b?.RecentActivityCount || 0),
      render: (v) => <Tag color="blue">{v ?? 0}</Tag>,
    },
    {
      title: "Total",
      dataIndex: "TotalActivityCount",
      key: "TotalActivityCount",
      width: 110,
      align: "right",
      sorter: (a, b) =>
        (a?.TotalActivityCount || 0) - (b?.TotalActivityCount || 0),
      render: (v) => <Tag color="geekblue">{v ?? 0}</Tag>,
    },
  ];

  const activityColumns = [
    {
      title: "User",
      dataIndex: "Name",
      key: "Name",
      width: 180,
      ellipsis: true,
      sorter: (a, b) => (a?.Name || "").localeCompare(b?.Name || ""),
      render: (v) => <Text strong>{v || "-"}</Text>,
    },
    {
      title: "Screen",
      dataIndex: "ScreenName",
      key: "ScreenName",
      width: 160,
      ellipsis: true,
      render: (v) => (v ? <Tag color="purple">{v}</Tag> : "-"),
    },
    {
      title: "Detail Name",
      dataIndex: "DetailName",
      key: "DetailName",
      width: 180,
      ellipsis: true,
      render: (v) => (v ? <Tag>{v}</Tag> : "-"),
    },
    {
      title: "Detail",
      dataIndex: "Detail",
      key: "Detail",
      ellipsis: { showTitle: false },
      render: (v) =>
        v ? (
          <Tooltip placement="topLeft" title={v}>
            <span>{v}</span>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "When",
      dataIndex: "DateTime",
      key: "DateTime",
      width: 220,
      sorter: (a, b) =>
        new Date(a?.DateTime || 0).getTime() -
        new Date(b?.DateTime || 0).getTime(),
      render: (dt) => (
        <Space direction="vertical" size={2}>
          <Text>{formatDateTime(dt)}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {timeAgo(dt)}
          </Text>
        </Space>
      ),
    },
  ];

  // Row keys
  const userRowKey = (row, idx) =>
    `${row?.Name || "user"}_${row?.LastActiveAt || ""}_${idx}`;
  const activityRowKey = (row, idx) =>
    `${row?.Name || "act"}_${row?.ScreenName || ""}_${
      row?.DateTime || ""
    }_${idx}`;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#fff",
        padding: isMobile ? 8 : 12,
      }}
    >
      {contextHolder}

      <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 8 }}>
        <Col flex="auto">
          <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
            User Activity Overview
          </Title>
          <Text type="secondary" style={{ fontSize: isMobile ? 12 : 13 }}>
            Recent activity by user and live feed
          </Text>
        </Col>
        <Col>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadAll(false)}
            loading={fetching}
            size={isMobile ? "small" : "middle"}
          >
            Refresh
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Table
            size="small"
            bordered
            rowKey={userRowKey}
            columns={userColumns}
            dataSource={filteredUserCounts}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            sticky
            scroll={{ x: 720 }}
            title={() => (
              <Row
                align="middle"
                justify="space-between"
                style={{ width: "100%" }}
              >
                <Col>
                  <Space direction="vertical" size={0}>
                    <Text strong>User Summary</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {filteredUserCounts.length} user
                      {filteredUserCounts.length === 1 ? "" : "s"}
                    </Text>
                  </Space>
                </Col>
                <Col>
                  <Input
                    allowClear
                    size={isMobile ? "small" : "middle"}
                    placeholder="Search user..."
                    value={searchUsers}
                    onChange={(e) => setSearchUsers(e.target.value)}
                    style={{ width: isMobile ? 160 : 240 }}
                    prefix={<UserOutlined />}
                  />
                </Col>
              </Row>
            )}
            style={{ marginBottom: 12 }}
          />

          <Table
            size="small"
            bordered
            rowKey={activityRowKey}
            columns={activityColumns}
            dataSource={filteredActivities}
            pagination={{ pageSize: 15, showSizeChanger: true }}
            sticky
            scroll={{ x: 960 }}
            title={() => (
              <Row
                align="middle"
                justify="space-between"
                style={{ width: "100%" }}
              >
                <Col>
                  <Space direction="vertical" size={0}>
                    <Text strong>Recent Activities</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {filteredActivities.length} record
                      {filteredActivities.length === 1 ? "" : "s"}
                    </Text>
                  </Space>
                </Col>
                <Col>
                  <Input
                    allowClear
                    size={isMobile ? "small" : "middle"}
                    placeholder="Search (name, screen, detail)..."
                    value={searchActivities}
                    onChange={(e) => setSearchActivities(e.target.value)}
                    style={{ width: isMobile ? 180 : 320 }}
                  />
                </Col>
              </Row>
            )}
          />
        </>
      )}
    </div>
  );
};

export default UserActivityOverView;
